import React, { useState, useEffect, useCallback } from 'react';
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube } from "react-icons/fa";
import { FiTrash2 } from 'react-icons/fi';
import { getDynamicFooter, addUpdateFooter } from "../services/ClientService";
import SnackBar from "./SnackBar";
import ConfirmModal from "./ConfirmModal";

const DynamicFooter = () => {
    const [isEditing, setIsEditing] = useState(true);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewContent, setPreviewContent] = useState({ title: '', description: '' });
    const [snackBarMessage, setSnackBarMessage] = useState("");
    const [isSnackBarVisible, setIsSnackBarVisible] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ visible: false, message: '', onConfirm: null });

    // Updated initial state: sections have empty links arrays
    const [footerData, setFooterData] = useState({
        sections: [],
        socialLinks: [
            { icon: "FaFacebookF", href: "https://facebook.com" },
            { icon: "FaTwitter", href: "https://twitter.com" },
            { icon: "FaInstagram", href: "https://instagram.com" },
            { icon: "FaYoutube", href: "https://youtube.com" },
        ],
        copyright: `© ${new Date().getFullYear()} Your Company Name Powered by Zuntly. All Rights Reserved`,
    });

    // Function to handle changes in input fields
    const handleInputChange = useCallback((sectionIndex, type, itemIndex, field, value) => {
        setFooterData(prevData => {
            const newData = { ...prevData };
            if (type === 'link') {
                newData.sections[sectionIndex].links[itemIndex][field] = value;
            } else if (type === 'sectionTitle') {
                newData.sections[sectionIndex].title = value;
            } else if (type === 'copyright') {
                newData.copyright = value;
            } else if (type === 'socialLink') {
                newData.socialLinks[itemIndex][field] = value;
            }
            return newData;
        });
    }, []);

    // Function to add a new link (Link Text & Description) inside a section
    const addNewLink = (sectionIndex) => {
        setFooterData(prevData => {
            const newSections = [...prevData.sections];
            newSections[sectionIndex].links.push({ text: "", description: "" });
            return { ...prevData, sections: newSections };
        });
    };

    // Function to delete a link from a section
    const deleteLink = (sectionIndex, linkIndex) => {
        setConfirmModal({
            visible: true,
            message: "Are you sure you want to delete this link?",
            onConfirm: () => {
                setFooterData(prevData => {
                    const newSections = [...prevData.sections];
                    newSections[sectionIndex].links = newSections[sectionIndex].links.filter((_, idx) => idx !== linkIndex);
                    return { ...prevData, sections: newSections };
                });
                setConfirmModal(prev => ({ ...prev, visible: false }));
            }
        });
    };

    // Function to add a new section (with empty title and links)
    const addNewSection = () => {
        setFooterData(prevData => ({
            ...prevData,
            sections: [
                ...prevData.sections,
                { title: "", links: [] }
            ]
        }));
    };

    // Function to delete a section
    const deleteSection = (sectionIndex) => {
        setConfirmModal({
            visible: true,
            message: "Are you sure you want to delete this section?",
            onConfirm: () => {
                setFooterData(prevData => ({
                    ...prevData,
                    sections: prevData.sections.filter((_, index) => index !== sectionIndex)
                }));
                setConfirmModal(prev => ({ ...prev, visible: false }));
            }
        });
    };

    // Function to handle submit button click
    const handleSubmit = async () => {
        console.log("Footer Data Submitted:", footerData);
        try {
            const footerAPI = await addUpdateFooter(footerData);
            if (footerAPI?.success) {
                setSnackBarMessage("Changes saved successfully!");
                setIsSnackBarVisible(true); // Show SnackBar
                setTimeout(() => {
                    setIsSnackBarVisible(false); // Hide SnackBar after 3 seconds
                }, 4000);
            } else {
                const errorMessage = footerAPI?.message || "Failed to save footer content.";
                setSnackBarMessage(`Error: ${errorMessage}`);
                setIsSnackBarVisible(true);
            }
        } catch (error) {
            const errorMessage = error?.message || "Unexpected error occurred while saving footer.";
            setSnackBarMessage(`Error: ${errorMessage}`);
            setIsSnackBarVisible(true);
        }
    };

    const socialIcons = {
        FaFacebookF: FaFacebookF,
        FaTwitter: FaTwitter,
        FaInstagram: FaInstagram,
        FaYoutube: FaYoutube,
    };

    const renderContent = (content) => {
        return content.split('\n').map((line, index) => (
            <span key={index}>
                {line}
                <br />
            </span>
        ));
    };

    // Generic function to handle link clicks in preview mode
    const handleLinkClick = (linkText, description) => {
        if (!isEditing && description) {
            setPreviewContent({ title: linkText, description: description });
            setIsPreviewOpen(true);
        }
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
    };


    useEffect(() => {
        loadDynamicFooter();
    }, []);

    const loadDynamicFooter = async () => {
        try {
            const data = await getDynamicFooter();
            console.log(data);
            if (data.success && data.data && data) {
                let footer = data.data
                delete footer._id;
                delete footer.masterId;
                // Remove contactDetails from loaded data if present
                footer.sections.forEach(section => {
                    delete section.contactDetails;
                });
                setFooterData(footer);
            }
        } catch (error) {
            console.error('Error while loading dynamic footer:', error);
        }
    };

    return (
        <>
        <footer className="bg-gray-900 text-gray-300 py-10 relative scrollbar-hide overflow-hidden">
            {/* Generic Preview Page */}
            {isPreviewOpen && (
                <div className="fixed top-0 left-0 w-full h-full bg-white z-50 overflow-auto p-10 scrollbar-hide">
                    <div className="max-w-3xl mx-auto">
                        <div className='flex justify-between items-center mb-5'>
                            <h2 className="text-2xl font-bold text-gray-900">{previewContent.title}</h2>
                            <button
                                onClick={closePreview}
                                className="relative text-gray-500 font-bold dark:text-white bg-transparent rounded-full px-4 py-1.5 transition-all duration-300 ease-in-out group"
                            >
                                <span className="absolute inset-0 rounded-full border-2 border-gray-600 dark:border-white dark:group-hover:border-white group-hover:border-gray-600 transition-transform duration-300 ease-in-out transform scale-0 group-hover:scale-100"></span>
                                <span className="relative">Close</span>
                            </button>
                        </div>
                        <div className="text-gray-700 whitespace-pre-line">
                            {renderContent(previewContent.description)}
                        </div>
                    </div>
                </div>
            )}

            <div className="container mx-auto px-6 lg:px-12">
                <div className="absolute top-0 right-0 mr-6">
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="bg-yellow-400 text-gray-900 font-semibold px-4 py-2 rounded-md rounded-tl-none rounded-tr-none hover:bg-yellow-500 focus:outline-none"
                    >
                        {isEditing ? 'Preview Mode' : 'Edit Mode'}
                    </button>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 gap-8 mb-8">
                    {footerData.sections.map((section, sectionIndex) => (
                        <div key={sectionIndex}>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-white">
                                    {isEditing ? (
                                        <input
                                            type="text"
                                            value={section.title}
                                            onChange={(e) => handleInputChange(sectionIndex, 'sectionTitle', null, null, e.target.value)}
                                            className="bg-transparent border-b border-gray-500 text-white focus:border-yellow-400 focus:outline-none"
                                            placeholder="Section Name"
                                        />
                                    ) : (
                                        section.title
                                    )}
                                </h3>
                                {isEditing && (
                                    <button
                                        onClick={() => deleteSection(sectionIndex)}
                                        className="text-red-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                                    >
                                        <FiTrash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            <ul className="space-y-3">
                                {section.links.map((link, linkIndex) => (
                                    <li key={linkIndex} className="group">
                                        {isEditing ? (
                                            <div className="flex flex-col">
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() => deleteLink(sectionIndex, linkIndex)}
                                                        className="text-red-500 hover:text-red-600 p-1 rounded-full hover:bg-red-50 transition-colors"
                                                    >
                                                        <FiTrash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                <input
                                                    type="text"
                                                    value={link.text}
                                                    onChange={(e) => handleInputChange(sectionIndex, 'link', linkIndex, 'text', e.target.value)}
                                                    className="hover:text-yellow-400 bg-transparent border-b border-gray-700 text-gray-300 hover:border-yellow-400 focus:outline-none w-full"
                                                    placeholder="Link Text"
                                                />
                                                <textarea
                                                    value={link.description}
                                                    onChange={(e) => handleInputChange(sectionIndex, 'link', linkIndex, 'description', e.target.value)}
                                                    className="hover:text-yellow-400 bg-transparent border-b border-gray-700 text-gray-300 hover:border-yellow-400 focus:outline-none w-full mt-1 text-sm resize-y min-h-[70px] scrollbar-hide"
                                                    placeholder="Description"
                                                    rows="3"
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className="text-gray-300 hover:text-yellow-400 transition-colors cursor-pointer"
                                                onClick={() => handleLinkClick(link.text, link.description)}
                                            >
                                                <div className="font-medium mb-1">{link.text}</div>
                                            </div>
                                        )}
                                    </li>
                                ))}
                                {/* Button to add a new Link inside this section (only in edit mode) */}
                                {isEditing && (
                                    <button
                                        onClick={() => addNewLink(sectionIndex)}
                                        className="mt-2 text-sm text-yellow-400 hover:underline"
                                    >
                                        + Add Link
                                    </button>
                                )}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* New Section button (only in edit mode) */}
                {isEditing && (
                    <div className="mt-6 text-center">
                        <button
                            onClick={addNewSection}
                            className="bg-yellow-400 text-gray-900 font-semibold px-6 py-2 rounded-md hover:bg-yellow-500 focus:outline-none"
                        >
                            New Section
                        </button>
                    </div>
                )}

                <div className="border-t border-gray-700 my-6"></div>

                <div className="flex md:space-x-6 mb-4 md:mb-0 flex-wrap max-md:flex-col max-md:gap-3">
                    {footerData.socialLinks.map((socialLink, socialIndex) => {
                        const IconComponent = socialIcons[socialLink.icon];
                        return IconComponent ? (
                            <div key={socialIndex} className="flex items-center gap-2 max-md:mb-2">
                                <a
                                    href={socialLink.href}
                                    className="w-8 h-8 flex justify-center items-center rounded-full bg-gray-700 hover:bg-yellow-400 transition-all"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <IconComponent className="text-white" />
                                </a>
                                {isEditing && (
                                    <input
                                        type="text"
                                        value={socialLink.href}
                                        onChange={(e) => handleInputChange(null, 'socialLink', socialIndex, 'href', e.target.value)}
                                        className="bg-transparent border-b border-gray-700 text-gray-300 focus:border-yellow-400 focus:outline-none"
                                        placeholder="Social Link URL"
                                    />
                                )}
                            </div>
                        ) : null;
                    })}
                </div>

                <p className="text-sm text-center mt-4">
                    {isEditing ? (
                        <input
                            type="text"
                            value={footerData.copyright}
                            onChange={(e) => handleInputChange(null, 'copyright', null, null, e.target.value)}
                            className="bg-transparent border-b border-gray-700 text-gray-300 focus:border-yellow-400 focus:outline-none w-full"
                        />
                    ) : (
                        footerData.copyright
                    )}
                </p>

                {!isEditing && (
                    <div className="text-center mt-6">
                        <button
                            onClick={handleSubmit}
                            className="bg-green-400 text-gray-900 font-semibold px-6 py-2 rounded-md hover:bg-green-500 focus:outline-none"
                        >
                            Submit Footer Data
                        </button>
                    </div>
                )}

                {/* SnackBar Component */}
                <SnackBar
                    message={snackBarMessage}
                    isVisible={isSnackBarVisible}
                    onClose={() => setIsSnackBarVisible(false)}
                />
            </div>
        </footer>

        <ConfirmModal
            visible={confirmModal.visible}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(prev => ({ ...prev, visible: false }))}
            cancelButtonText="Cancel"
            confirmButtonText="Delete"
        />
        </>
    );
};

export default DynamicFooter;